import { AAMediaTypes, type AACampaign } from '@akross/artemis-ads-sdk';
import { AppFormatter } from './app.formatter';
import { logger } from './logger.util';

// ============================================================================
// CACHE
// ============================================================================

/**
 * Cache unificado para armazenar campanhas do Artemis (AACampaign)
 * Usado tanto para campanhas quanto para challenges
 * Evita chamadas desnecessárias à API na página de detalhes
 */
class CampaignCache {
  private campaigns: AACampaign[] = [];

  /**
   * Armazena campanhas no cache
   */
  setCampaigns(campaigns: AACampaign[]): void {
    logger.debug('[CampaignCache] Armazenando campanhas no cache:', campaigns.length);
    this.campaigns = campaigns;
  }

  /**
   * Busca uma campanha pelo ID ou UUID no cache
   * Aceita tanto id quanto campaignUuid
   */
  getCampaign(campaignId: string): AACampaign | null {
    logger.debug('[CampaignCache] Buscando campanha no cache, campaignId:', campaignId);

    const campaign = this.campaigns.find(
      campaign => campaign.id === campaignId || campaign.campaignUuid === campaignId
    ) || null;

    if (!campaign) {
      logger.warnWithContext('CampaignCache', 'Campanha não encontrada no cache, campaignId:', campaignId);
    }

    return campaign;
  }

  /**
   * Atualiza uma campanha específica no cache
   * Útil para atualizar estado de mídias visualizadas, etc.
   */
  updateCampaign(updatedCampaign: AACampaign): void {
    const index = this.campaigns.findIndex(
      c => c.campaignUuid === updatedCampaign.campaignUuid || c.id === updatedCampaign.id
    );
    if (index >= 0) {
      this.campaigns[index] = updatedCampaign;
      logger.debug('[CampaignCache] Campanha atualizada no cache');
    } else {
      logger.warnWithContext('CampaignCache', 'Tentativa de atualizar campanha não encontrada no cache');
    }
  }
}

export const campaignCache = new CampaignCache();

// ============================================================================
// CÁLCULOS
// ============================================================================

/**
 * Obtém a unit dos benefitOffers de uma campanha
 * Assumindo que todos os benefícios sempre têm a mesma unit
 * @returns A unit do primeiro benefitOffer ou string vazia se não houver
 */
export function getBenefitUnit(campaign: AACampaign): string {
  const firstOffer = campaign.benefitOffers?.[0];
  return firstOffer?.unit || '';
}

/**
 * Calcula o prêmio total de uma campanha baseado nos benefitOffers
 * Soma todas as quantidades, assumindo que todos têm a mesma unit
 */
export function calculateTotalPrize(campaign: AACampaign): number {
  return campaign.benefitOffers?.reduce((sum, offer) => {
    return sum + parseInt(offer.quantity || '0', 10);
  }, 0) || 0;
}

/**
 * Calcula o número de mídias disponíveis (não visualizadas e com tipo)
 */
export function calculateTotalAvailableMedias(campaign: AACampaign): number {
  const medias = campaign.mainData?.media || [];
  return medias.filter(m => !m.viewed && m.type != null).length;
}

/**
 * Calcula o número total de mídias suportadas (com tipo)
 */
export function calculateTotalSupportedMedias(campaign: AACampaign): number {
  const medias = campaign.mainData?.media || [];
  return medias.filter(m => m.type != null).length;
}

/**
 * Calcula o número de mídias já visualizadas
 */
export function calculateTotalWatchedMedias(campaign: AACampaign): number {
  const medias = campaign.mainData?.media || [];
  return medias.filter(m => m.viewed === true).length;
}

/**
 * Verifica se a campanha tem mídias disponíveis
 */
export function hasAvailableMedia(campaign: AACampaign): boolean {
  return calculateTotalAvailableMedias(campaign) > 0;
}

/**
 * Calcula o progresso da campanha (0 a 1)
 */
export function calculateProgress(campaign: AACampaign): number {
  const totalSupported = calculateTotalSupportedMedias(campaign);
  const watched = calculateTotalWatchedMedias(campaign);

  if (totalSupported === 0) return 0;
  return watched / totalSupported;
}

// ============================================================================
// IMAGENS
// ============================================================================

/**
 * Obtém a URL da imagem de uma campanha seguindo a ordem de prioridade:
 * 1. Mídia tipo image: content.url
 * 2. Imagem da primeira mídia disponível (layout.header.image)
 * 3. Header do layout principal (mainData.layout.header.image)
 * 4. Ícone do layout (mainData.layout.icon)
 * 5. Header do layout simples (layout.header)
 * 6. ThumbUrl da campanha
 *
 * @param campaign - Campanha (AACampaign)
 * @returns URL da imagem ou string vazia se não encontrar
 */
export function getCampaignImage(campaign: AACampaign): string {
  if ('mainData' in campaign && campaign.mainData) {
    // Tentar obter imagem da primeira mídia disponível
    const availableMedias = campaign.mainData.media?.filter((m: any) => !m.viewed && m.type != null) || [];
    const firstAvailable = availableMedias[0];

    // Mídia tipo image (banner): usar content.url
    if (firstAvailable?.type === AAMediaTypes.BANNER && firstAvailable?.content?.url) {
      return firstAvailable.content.url;
    }

    if (firstAvailable?.layout?.header?.image) {
      return firstAvailable.layout.header.image;
    }

    // Fallback para header do layout principal
    if (campaign.mainData.layout?.header?.image) {
      return campaign.mainData.layout.header.image;
    }

    // Fallback para ícone do layout
    if (campaign.mainData.layout?.icon) {
      return campaign.mainData.layout.icon;
    }
  }

  // Fallback para campanhas AACampaign simples
  if ('layout' in campaign && campaign.layout?.header) {
    return campaign.layout.header;
  }

  if ('thumbUrl' in campaign && campaign.thumbUrl) {
    return campaign.thumbUrl;
  }

  // Retornar vazio se não encontrar
  return '';
}

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Verifica se uma campanha tem apenas mídias do tipo external_redirect
 * @param campaign Campanha a ser verificada
 * @returns true se todas as mídias são external_redirect, false caso contrário
 */
export function hasOnlyExternalRedirectMedias(campaign: AACampaign): boolean {
  const medias = campaign.mainData?.media || [];

  if (medias.length === 0) {
    return false; // Se não tem mídias, não é external_redirect
  }

  // Verificar se todas as mídias são do tipo external_redirect
  return medias.every((media: any) => media.type === 'external_redirect');
}

/**
 * Determina qual página de detalhe usar baseado no tipo de mídia da campanha
 * @param campaign Campanha a ser verificada
 * @returns 'external_redirect' se todas as mídias são external_redirect, 'media' caso contrário
 */
export function getCampaignDetailPageType(campaign: AACampaign | null): 'external_redirect' | 'media' | null {
  if (!campaign) {
    return null;
  }

  return hasOnlyExternalRedirectMedias(campaign) ? 'external_redirect' : 'media';
}

// ============================================================================
// FORMATAÇÃO
// ============================================================================

/**
 * Utilitários de formatação para campanhas/challenges
 */
export class CampaignFormatter {
  /**
   * Formata prêmio
   */
  static formatPrize(prize: number): string {
    return AppFormatter.formatPrize(prize);
  }

  /**
   * Formata contagem de mídias
   */
  static formatMediaCount(count: number): string {
    if (count === 1) {
      return '1 anúncio';
    }
    return `${count} anúncios`;
  }

  /**
   * Formata texto de prêmio
   */
  static formatPrizeText(prize: number, mediaPrize: number, currencyName: string = 'MB'): string {
    if (mediaPrize <= 0) {
      return `${this.formatPrize(prize)} ${currencyName}`;
    }
    if (prize > 0) {
      return `+${this.formatPrize(mediaPrize)} ${currencyName} extras`
    }
    return `Até ${this.formatPrize(mediaPrize)} ${currencyName}`;
  }

  /**
   * Calcula progresso da campanha
   */
  static calculateProgress(campaign: AACampaign): number {
    const totalSupported = calculateTotalSupportedMedias(campaign);
    const watched = calculateTotalWatchedMedias(campaign);

    if (totalSupported === 0) return 0;
    return watched / totalSupported;
  }

  /**
   * Formata tempo restante (countdown)
   */
  static formatTimeLeft(hours: number, minutes: number, seconds: number): string {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}

// Alias para manter compatibilidade com código existente
export const ChallengeFormatter = CampaignFormatter;
